import { nanoid } from 'nanoid'

import {
  DynamicBlock as DynamicBlockType,
  Image,
  PermissionContext,
} from '@tribeplatform/gql-client/types'
import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import { useAvailableDynamicBlocks } from '@tribeplatform/react-sdk/hooks'
import {
  useBlock,
  useSlate,
  useSlateKitContext,
} from '@tribeplatform/react-slate-kit/hooks'
import {
  BlockType,
  RegisteredBlockWithName,
  UnknownProps,
} from '@tribeplatform/react-slate-kit/types'
import { In } from '@tribeplatform/react-slate-kit/utils'
import { Accordion } from '@tribeplatform/react-ui-kit/Accordion'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'

import { useCustomizerNavigation, useCustomizerSpace } from '../hooks/index.js'
import { AddBlocksSidebarItem } from './AddBlocksSidebarItem.js'
import { AddDynamicBlocksSidebarItem } from './AddDynamicBlocksSidebarItem.js'
import {
  createNewBlockInitialChildren,
  getUniqueNameInSlate,
  initialPropsFactory,
} from './utils.js'

export const AddBlockEditor = () => {
  const slateKitProps = useSlateKitContext()
  const { space, editSpace } = useCustomizerSpace()
  const { editHeader } = useCustomizerNavigation()
  const {
    addBlock,
    compiledSlate,
    extraContext: {
      slate: { newBlockPosition },
    },
  } = useSlate<{
    newBlockPosition?: number
  }>()
  const {
    block: { id, children },
  } = useBlock()
  const createInitialProps = useStaticIntl(initialPropsFactory)

  const blocks = slateKitProps.kit.getAvailableBlocks({
    context: {
      size: 'md',
      spaceType: editSpace && space ? space.type : null,
      header: editHeader,
    },
    filter: {
      type: In([BlockType.Block, BlockType.Layout]),
      usable: true,
    },
    orderBy: 'displayName',
  })
  const registeredDynamicBlock = slateKitProps.kit.getDynamicBlock()

  const { data: dynamicBlocks } = useAvailableDynamicBlocks({
    variables: {
      context:
        editSpace && space ? PermissionContext.SPACE : PermissionContext.POST,
    },
    fields: {
      app: {
        favicon: 'all',
        image: 'all',
      },
      favicon: 'all',
      image: 'all',
    },
  })

  const blocksBySections: {
    id: string
    title: string
    appId: string | null
    blocks: RegisteredBlockWithName<UnknownProps>[]
    dynamicBlocks: DynamicBlockType[]
    default?: boolean
    iconUrl?: string
  }[] = [
    {
      id: 'building-blocks',
      title: 'Building blocks',
      appId: null,
      blocks,
      dynamicBlocks: [],
      default: true,
    },
  ]
  dynamicBlocks?.forEach(dynamicBlock => {
    const currentAppIndex = blocksBySections.findIndex(
      s => s.appId === dynamicBlock.app.id,
    )
    if (currentAppIndex === -1) {
      blocksBySections.push({
        id: dynamicBlock.app.id,
        title: `${dynamicBlock.app.name} blocks`,
        appId: dynamicBlock.app.id,
        blocks: [],
        dynamicBlocks: [dynamicBlock],
        iconUrl: ((dynamicBlock.app.favicon ?? dynamicBlock.app.image) as Image)
          ?.url,
      })
    } else {
      blocksBySections[currentAppIndex].dynamicBlocks.push(dynamicBlock)
    }
  })

  const addNewBlock = (block: RegisteredBlockWithName<UnknownProps>) => {
    const newBlockId = nanoid()
    const newBlockName = getUniqueNameInSlate({
      blockName: block.config.displayName,
      blocks: compiledSlate.blocks,
    })

    // Some blocks can have initial children by default
    // for example Section block must have two Column blocks when created
    const initialChildren = createNewBlockInitialChildren(
      block.name,
      slateKitProps.kit,
    )

    return addBlock(
      id,
      newBlockPosition ?? children.length,
      {
        rootBlock: newBlockId,
        blocks: [
          ...initialChildren,
          {
            id: newBlockId,
            name: block.name,
            props: createInitialProps({ block, space }),
            extraProps: {
              displayName: newBlockName,
            },
            children: initialChildren.map(({ id }) => id),
          },
        ],
      },
      block.config,
    )
  }
  const addNewDynamicBlock = (block: DynamicBlockType) => {
    const newBlockId = nanoid()
    const newBlockName = getUniqueNameInSlate({
      blockName: registeredDynamicBlock.name,
      blocks: compiledSlate.blocks,
    })

    return addBlock(
      id,
      newBlockPosition ?? children.length,
      {
        rootBlock: newBlockId,
        blocks: [
          {
            id: newBlockId,
            name: registeredDynamicBlock.name,
            props: {
              appId: block.appId,
              blockKey: block.key,
              staffOnly: block.staffOnly,
            },
            extraProps: {
              displayName: newBlockName,
            },
          },
        ],
      },
      registeredDynamicBlock.config,
    )
  }

  return (
    <div className="-ms-3 -me-3 scroll-smooth">
      {blocksBySections.map(item => (
        <Accordion
          key={item.id}
          variant="chevron"
          iconAlign="trailing"
          defaultOpen={item.default}
          inset="inset"
          className="space-y-0"
        >
          <Accordion.Button className="px-3 rounded-none">
            <div className="gap-2 flex items-center">
              {item.appId && (
                <Avatar
                  size="xl"
                  rounded="sm"
                  name={item.title}
                  src={item.iconUrl}
                  className="flex"
                />
              )}
              <span>{item.title}</span>
            </div>
          </Accordion.Button>
          <Accordion.Panel className="-m-2">
            {item.blocks.map(block => (
              <AddBlocksSidebarItem
                key={block.name}
                block={block}
                onClick={() => addNewBlock(block)}
              />
            ))}
            {item.dynamicBlocks.map(dynamicBlock => (
              <AddDynamicBlocksSidebarItem
                key={dynamicBlock.id}
                dynamicBlock={dynamicBlock}
                onClick={() => addNewDynamicBlock(dynamicBlock)}
              />
            ))}
          </Accordion.Panel>
        </Accordion>
      ))}
    </div>
  )
}
